import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import DocumentPreviewModal from './DocumentPreviewModal';
import DocumentShareModal from './DocumentShareModal';

const t = (key) => {
  const translations = {
    'documents.secureStorage': { fr: 'Stockage Sécurisé', ar: 'التخزين الآمن' },
    'documents.searchDocuments': { fr: 'Rechercher des documents...', ar: 'البحث عن المستندات...' },
    'documents.noDocumentsFound': { fr: 'Aucun document trouvé', ar: 'لم يتم العثور على مستندات' },
    'documents.version': { fr: 'Version', ar: 'الإصدار' },
    'documents.preview': { fr: 'Aperçu', ar: 'معاينة' },
    'documents.download': { fr: 'Télécharger', ar: 'تحميل' },
    'documents.share': { fr: 'Partager', ar: 'مشاركة' }
  };
  return translations?.[key] || key;
};

const DocumentStorageArea = ({ currentLanguage }) => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const handlePreview = (doc) => {
    setSelectedDocument(doc);
    setPreviewModalOpen(true);
  };

  const handleDownload = (doc) => {
    alert(currentLanguage === 'fr' ?
    `Téléchargement de: ${doc?.name}` :
    `تحميل: ${doc?.name}`);
  };

  const handleShare = (doc) => {
    setSelectedDocument(doc);
    setShareModalOpen(true);
  };

  const documents = [
  {
    id: 'doc-001',
    name: 'Contrat_Omra_Ahmed_Benali.pdf',
    type: 'contract',
    pilgrim: {
      name: 'Ahmed Benali',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_128b56b6a-1763292153045.png",
      avatarAlt: 'Professional headshot of Moroccan man with short black hair wearing traditional white djellaba'
    },
    size: '2.4 MB',
    uploadDate: new Date('2026-01-15'),
    status: 'signed',
    version: 2
  },
  {
    id: 'doc-002',
    name: 'Attestation_Hajj_Fatima_Zahra.pdf',
    type: 'attestation',
    pilgrim: {
      name: 'Fatima Zahra',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1eec778e9-1766069487817.png",
      avatarAlt: 'Professional headshot of Moroccan woman with hijab in elegant beige color'
    },
    size: '1.8 MB',
    uploadDate: new Date('2026-01-20'),
    status: 'pending',
    version: 1
  },
  {
    id: 'doc-003',
    name: 'Liste_Officielle_Groupe_A.pdf',
    type: 'official_list',
    pilgrim: null,
    size: '5.2 MB',
    uploadDate: new Date('2026-01-25'),
    status: 'completed',
    version: 3
  },
  {
    id: 'doc-004',
    name: 'Convocation_Youssef_Alami.pdf',
    type: 'convocation',
    pilgrim: {
      name: 'Youssef Alami',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15698c709-1763292831085.png",
      avatarAlt: 'Professional headshot of Moroccan man with beard wearing navy blue suit'
    },
    size: '1.2 MB',
    uploadDate: new Date('2026-01-28'),
    status: 'sent',
    version: 1
  }];


  const filterOptions = [
  { value: 'all', label: { fr: 'Tous', ar: 'الكل' } },
  { value: 'contract', label: { fr: 'Contrats', ar: 'العقود' } },
  { value: 'attestation', label: { fr: 'Attestations', ar: 'الشهادات' } },
  { value: 'official_list', label: { fr: 'Listes officielles', ar: 'القوائم الرسمية' } },
  { value: 'convocation', label: { fr: 'Convocations', ar: 'الاستدعاءات' } }];


  const statusConfig = {
    signed: {
      label: { fr: 'Signé', ar: 'موقع' },
      color: 'text-success',
      bgColor: 'bg-success/10',
      icon: 'CheckCircle'
    },
    pending: {
      label: { fr: 'En attente', ar: 'قيد الانتظار' },
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      icon: 'Clock'
    },
    completed: {
      label: { fr: 'Complété', ar: 'مكتمل' },
      color: 'text-success',
      bgColor: 'bg-success/10',
      icon: 'CheckCircle2'
    },
    sent: {
      label: { fr: 'Envoyé', ar: 'مُرسل' },
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      icon: 'Send'
    }
  };

  const filteredDocuments = documents?.filter((doc) => {
    const matchesFilter = selectedFilter === 'all' || doc?.type === selectedFilter;
    const matchesSearch = doc?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    doc?.pilgrim?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const formatDate = (date) => {
    // Always use en-US locale for universal date format (Western numerals)
    return new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })?.format(date);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-warm">
      <div className="p-4 md:p-6 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          {t('documents.secureStorage')}
        </h2>

        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Icon
              name="Search"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

            <input
              type="text"
              placeholder={t('documents.searchDocuments')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20" />

          </div>

          <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0">
            {filterOptions?.map((option) =>
            <button
              key={option?.value}
              onClick={() => setSelectedFilter(option?.value)}
              className={`
                  px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-smooth
                  ${selectedFilter === option?.value ?
              'bg-primary text-primary-foreground shadow-warm' :
              'bg-muted text-muted-foreground hover:bg-muted/80'}
                `
              }>

                {option?.label?.[currentLanguage]}
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
        {filteredDocuments?.length === 0 ?
        <div className="p-8 text-center">
            <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              {t('documents.noDocumentsFound')}
            </p>
          </div> :

        filteredDocuments?.map((doc) => {
          const status = statusConfig?.[doc?.status];
          return (
            <div key={doc?.id} className="p-4 hover:bg-muted/50 transition-smooth">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="FileText" size={24} className="text-primary" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-foreground truncate mb-1">
                          {doc?.name}
                        </h3>
                        {doc?.pilgrim &&
                      <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full overflow-hidden">
                              <Image
                            src={doc?.pilgrim?.avatar}
                            alt={doc?.pilgrim?.avatarAlt}
                            className="w-full h-full object-cover" />

                            </div>
                            <span className="caption text-muted-foreground">
                              {doc?.pilgrim?.name}
                            </span>
                          </div>
                      }
                      </div>

                      <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${status?.bgColor} flex-shrink-0`}>
                        <Icon name={status?.icon} size={14} className={status?.color} />
                        <span className={`caption font-medium ${status?.color}`}>
                          {status?.label?.[currentLanguage]}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                      <span className="flex items-center gap-1">
                        <Icon name="HardDrive" size={14} />
                        {doc?.size}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        {formatDate(doc?.uploadDate)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="GitBranch" size={14} />
                        {t('documents.version')} {doc?.version}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                      onClick={() => handlePreview(doc)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-smooth text-sm">
                        <Icon name="Eye" size={14} />
                        {t('documents.preview')}
                      </button>
                      <button
                      onClick={() => handleDownload(doc)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-muted text-foreground rounded-md hover:bg-muted/80 transition-smooth text-sm">
                        <Icon name="Download" size={14} />
                        {t('documents.download')}
                      </button>
                      <button
                      onClick={() => handleShare(doc)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-muted text-foreground rounded-md hover:bg-muted/80 transition-smooth text-sm">
                        <Icon name="Share2" size={14} />
                        {t('documents.share')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>);

        })
        }
      </div>

      <DocumentPreviewModal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        document={selectedDocument}
        currentLanguage={currentLanguage} />
      

      <DocumentShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        document={selectedDocument}
        currentLanguage={currentLanguage} />
      
    </div>);


};

export default DocumentStorageArea;